import * as React from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Props for the Breadcrumb root component
 */
export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Maximum number of items before truncation (default 4)
   * When exceeded, middle items collapse to "..." dropdown
   */
  maxItems?: number;
  /**
   * Custom separator between items (default "/")
   */
  separator?: React.ReactNode;
  /**
   * Breadcrumb items (BreadcrumbItem components)
   */
  children: React.ReactNode;
}

/**
 * Props for individual breadcrumb items
 */
export interface BreadcrumbItemProps extends React.HTMLAttributes<HTMLLIElement> {
  /**
   * If provided, renders as a link
   */
  href?: string;
  /**
   * Marks this as the current page (last item)
   * Renders without link and with aria-current="page"
   */
  isCurrent?: boolean;
  /**
   * Content of the breadcrumb item
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Context for sharing breadcrumb configuration
 */
interface BreadcrumbContextValue {
  separator: React.ReactNode;
}

const BreadcrumbContext = React.createContext<BreadcrumbContextValue | null>(null);

/**
 * Hook to access breadcrumb context
 */
function useBreadcrumbContext() {
  const context = React.useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('BreadcrumbItem must be used within a Breadcrumb component');
  }
  return context;
}

/**
 * Separator component
 */
function BreadcrumbSeparator({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <li
      aria-hidden="true"
      className="mx-2 text-secondary-400 text-sm select-none"
    >
      {children}
    </li>
  );
}

BreadcrumbSeparator.displayName = 'Breadcrumb.Separator';

/**
 * Ellipsis dropdown for truncated items
 */
interface EllipsisMenuProps {
  items: React.ReactElement<BreadcrumbItemProps>[];
  separator: React.ReactNode;
}

function EllipsisMenu({ items, separator }: Readonly<EllipsisMenuProps>) {
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      /* v8 ignore start */
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        /* v8 ignore stop */
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on Escape key
  React.useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <>
      <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>
      <li className="relative">
        <div ref={menuRef}>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-1 text-sm text-secondary-500 hover:text-secondary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 rounded px-1 py-0.5 transition-colors duration-150"
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-label="Show more breadcrumb items"
          >
            <span>...</span>
            <ChevronDown
              size={14}
              className={`transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {isOpen && (
            <div
              className="absolute left-0 top-full mt-1 min-w-[160px] bg-secondary-100 border border-secondary-200 rounded-lg shadow-lg py-1 z-50"
              role="menu"
            >
              {items.map((item, index) => {
                const { href, children, className = '' } = item.props;
                return (
                  <div key={href || `breadcrumb-${index}`} role="menuitem">
                    {href ? (
                      <a
                        href={href}
                        className={`block px-3 py-2 text-sm text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700 transition-colors ${className}`}
                        onClick={() => setIsOpen(false)}
                      >
                        {children}
                      </a>
                    ) : (
                      <span
                        className={`block px-3 py-2 text-sm text-secondary-500 ${className}`}
                      >
                        {children}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </li>
    </>
  );
}

/**
 * BreadcrumbItem component
 *
 * Individual breadcrumb item that can be a link or static text.
 */
const BreadcrumbItem = React.forwardRef<HTMLLIElement, BreadcrumbItemProps>(
  ({ href, isCurrent = false, className = '', children, ...props }, ref) => {
    const { separator } = useBreadcrumbContext();

    // Determine the content element based on href and isCurrent
    let content: React.ReactNode;

    if (isCurrent) {
      content = (
        <span
          className="text-sm font-medium text-secondary-800"
          aria-current="page"
        >
          {children}
        </span>
      );
    } else if (href) {
      content = (
        <a
          href={href}
          className="text-sm text-secondary-500 hover:text-secondary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 rounded transition-colors duration-150"
        >
          {children}
        </a>
      );
    } else {
      content = (
        <span className="text-sm text-secondary-500">
          {children}
        </span>
      );
    }

    const combinedClasses = ['flex items-center', className].filter(Boolean).join(' ');

    return (
      <>
        <li ref={ref} className={combinedClasses} {...props}>
          {content}
        </li>
        {!isCurrent && <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>}
      </>
    );
  }
);

BreadcrumbItem.displayName = 'Breadcrumb.Item';

/**
 * Breadcrumb root component
 *
 * A navigation component for displaying hierarchical page paths.
 * Supports truncation with an ellipsis dropdown when items exceed maxItems.
 *
 * @example
 * // Basic usage
 * <Breadcrumb>
 *   <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
 *   <Breadcrumb.Item href="/policies">Policies</Breadcrumb.Item>
 *   <Breadcrumb.Item isCurrent>POL-2024-001842</Breadcrumb.Item>
 * </Breadcrumb>
 *
 * @example
 * // With custom separator
 * <Breadcrumb separator=">">
 *   <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
 *   <Breadcrumb.Item isCurrent>Dashboard</Breadcrumb.Item>
 * </Breadcrumb>
 *
 * @example
 * // With truncation
 * <Breadcrumb maxItems={3}>
 *   <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
 *   <Breadcrumb.Item href="/section">Section</Breadcrumb.Item>
 *   <Breadcrumb.Item href="/category">Category</Breadcrumb.Item>
 *   <Breadcrumb.Item href="/subcategory">Subcategory</Breadcrumb.Item>
 *   <Breadcrumb.Item isCurrent>Current Page</Breadcrumb.Item>
 * </Breadcrumb>
 */
function BreadcrumbRoot({
  maxItems = 4,
  separator = '/',
  className = '',
  children,
  ...props
}: Readonly<BreadcrumbProps>) {
  const contextValue: BreadcrumbContextValue = React.useMemo(
    () => ({ separator }),
    [separator]
  );

  // Convert children to array and filter for valid BreadcrumbItem elements
  const childArray = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<BreadcrumbItemProps> =>
      React.isValidElement(child)
  );

  const totalItems = childArray.length;
  const shouldTruncate = totalItems > maxItems;

  // Determine which items to show
  let visibleItems: React.ReactNode;

  if (shouldTruncate && totalItems > 2) {
    // Always show first item
    const firstItem = childArray[0]!;
    // Always show last item (current)
    const lastItem = childArray[totalItems - 1]!;
    // Hidden items go in the ellipsis menu (all middle items)
    const hiddenItems = childArray.slice(1, totalItems - 1);

    visibleItems = (
      <>
        {/* First item */}
        <BreadcrumbItem
          key="first"
          {...firstItem.props}
        />
        {/* Ellipsis with dropdown */}
        <EllipsisMenu items={hiddenItems} separator={separator} />
        {/* Separator before last item */}
        <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>
        {/* Last item (current) - render without separator since isCurrent handles it */}
        <li className="flex items-center">
          <span
            className="text-sm font-medium text-secondary-800"
            aria-current="page"
          >
            {lastItem.props.children}
          </span>
        </li>
      </>
    );
  } else {
    // No truncation needed, render all items
    visibleItems = childArray.map((child, index) => {
      const isLast = index === totalItems - 1;
      return (
        <BreadcrumbItem
          key={child.key}
          {...child.props}
          isCurrent={isLast || child.props.isCurrent}
        />
      );
    });
  }

  const combinedClasses = ['', className].filter(Boolean).join(' ');

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      <nav
        aria-label="Breadcrumb"
        className={combinedClasses}
        {...props}
      >
        <ol className="flex items-center flex-wrap">
          {visibleItems}
        </ol>
      </nav>
    </BreadcrumbContext.Provider>
  );
}

BreadcrumbRoot.displayName = 'Breadcrumb';

/**
 * Breadcrumb compound component
 *
 * Usage:
 * - Breadcrumb.Item - Individual breadcrumb item (can be link or text)
 */
const Breadcrumb = Object.assign(BreadcrumbRoot, {
  Item: BreadcrumbItem,
});

export { Breadcrumb, BreadcrumbItem };
